import {
	Dialog,
	DialogActions,
	DialogBody,
	DialogContent,
	DialogSurface,
	Spinner,
} from '@fluentui/react-components';
import React, { useEffect, useState } from "react";
import { Constants } from '../common/Constants';
import { useKaiaContactFormDialogStyles } from './KaiaContactFormDialog.styles';

export interface KaiaContactFormDialogProps {
	isOpen: boolean;
	onClose: () => void;
}

/**
 * A dialog component that displays the KAiA contact form in an iframe.
 * 
 * @component
 * @param {boolean} isOpen - Controls whether the dialog is visible
 * @param {() => void} onClose - Callback function invoked when the dialog is closed
 * 
 * @remarks
 * This component manages loading state internally and displays a spinner while the iframe content is loading.
 * The loading state is reset whenever the dialog is opened after being closed.
 * 
 * @example
 * ```tsx
 * <KaiaContactFormDialog 
 *   isOpen={isDialogOpen} 
 *   onClose={() => setIsDialogOpen(false)} 
 * />
 * ```
 */
const KaiaContactFormDialog: React.FunctionComponent<KaiaContactFormDialogProps> = ({ isOpen, onClose }: KaiaContactFormDialogProps) => {
	const [isLoading, setIsLoading] = useState(true);
	const [closeRequested, setCloseRequested] = useState(true);
	const kaiaStyles = useKaiaContactFormDialogStyles();

	const handleClosing = () => {
		setCloseRequested(true);
		onClose();
	}

	const restartLoading = () => {
		if (isOpen && closeRequested) {
			setCloseRequested(false);
			setIsLoading(true)
		}
	}

	useEffect(restartLoading, [isOpen, closeRequested]);

	return (
		<Dialog open={isOpen}>
			<DialogSurface className={`in-shell-contact-form-container ${kaiaStyles.surface}`}>
			<DialogBody className="kt-ui-app dialog-content">
					<DialogActions className="in-shell-contact-form-dialog-header">
						<span onClick={() => handleClosing()}>
							+
						</span>
					</DialogActions>
				<DialogContent className="in-shell-contact-form-dialog-content">
					{isLoading ? (
							<Spinner className="in-shell-contact-form-spinner" label="Loading form..." />
					): <></>}
					<iframe
					onLoad={() => setIsLoading(false)}
					title={Constants.kaiaContactFormIframeTitle}
					loading='lazy'
					className={`${kaiaStyles.iframe} ${isLoading ? kaiaStyles.iframeLoading : kaiaStyles.iframeLoaded}`}
					src={Constants.KAIA_BANNER_CONTACT_FORM_LINK}>
					</iframe>
				</DialogContent>
				</DialogBody>
			</DialogSurface>
		</Dialog>
	)
}

export default KaiaContactFormDialog